import { useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';

import { useGetDrawerWidth } from '@makemydeal/dr-dash-components';
import { getReturnToOfferButton, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import * as bridgeUserSelectors from '@makemydeal/dr-dash-store/selectors/bridgeUserSelectors';
import { getVAutoAppraisals } from '@makemydeal/dr-dash-store/actions/vAutoActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import type { StateTree } from '@makemydeal/dr-dash-types';

import { SlideOutWrapper } from './SlideOutWrapper';
import { AppraisalSlideOutContent } from './AppraisalSlideOutContent';
import type { SlideOutComponentProps } from '../../types';

export { toIntegratedAppraisalUrl, getPostMessageTarget } from './appraisalSlideOutUtils';

export const AppraisalSlideOut = ({ isOpen, onClose }: SlideOutComponentProps) => {
    const dispatch = useDispatch();
    const store = useStore();
    const panelWidth = useGetDrawerWidth({
        large: 1200,
        extraLarge: 1200
    });

    // Refetch on the open -> closed transition rather than from `onClose`, so an appraisal saved in
    // vAuto reaches the pills and ACV on every close path, not just the ones that call the handler.
    const wasOpenRef = useRef(false);
    useEffect(() => {
        const hasJustClosed = wasOpenRef.current && !isOpen;
        wasOpenRef.current = isOpen;

        if (!hasJustClosed) {
            return;
        }

        const platformUserId = bridgeUserSelectors.getBridgeId(store.getState() as StateTree);
        if (platformUserId) {
            // Flagged as a refresh so the ACV fields show a loading indicator while it runs.
            dispatch(getVAutoAppraisals(MANAGER_VIEW_APP_PREFIX, platformUserId, true));
            // No Apply button here — closing applies the update.
            dispatch(interactionAnalyticsActionCreators.tradeInInfoUpdateApplied());
        }
    }, [isOpen, store, dispatch]);

    return (
        <SlideOutWrapper
            title="vAuto Appraisal"
            isOpen={isOpen}
            onClose={onClose}
            panelWidth={panelWidth}
            testId="appraisal-slideout"
            footer={getReturnToOfferButton(onClose)}
        >
            {isOpen ? <AppraisalSlideOutContent onClose={onClose} /> : undefined}
        </SlideOutWrapper>
    );
};
